import type { CompanyIdentifier } from "../types/company-identifier";

/**
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps a mirrored
 * copy in `api/src/common/utils/company-identifier.ts`, held to this one by
 * `api/src/common/utils/company-identifier-parity.spec.ts`.
 *
 * Keep both files in sync. A mismatch would have the chat composer open a card
 * for a message the API then refuses to resolve, or hand to the model a message
 * the API would have resolved without it.
 */

/**
 * Longest message still read as an identifier. Far above any SIRET, domain or
 * LinkedIn address, and the same cap the API puts on the request body.
 */
export const COMPANY_IDENTIFIER_MAX_LENGTH = 300;

/**
 * Sites whose domain names the platform, not the company a user means: a
 * profile, a map or an article about it. The prospecting page's quick lookup
 * excludes the same list (`EXCLUDED_LOOKUP_DOMAINS`, which the API mirror
 * imports), so the chat and the page agree on what a website is.
 */
export const COMPANY_IDENTIFIER_EXCLUDED_DOMAINS: readonly string[] = [
  "google.com",
  "google.fr",
  "facebook.com",
  "twitter.com",
  "x.com",
  "linkedin.com",
  "instagram.com",
  "youtube.com",
  "wikipedia.org",
  "amazon.com",
  "amazon.fr",
  "github.com",
  "reddit.com",
  "tiktok.com",
];

/**
 * What people type between the digits of a SIRET or a SIREN: spaces —
 * including the no-break ones a copy from a PDF or a registry page carries —
 * dots and dashes.
 */
const REGISTRY_NUMBER_SEPARATORS = /[\s\u00A0\u202F.-]/g;

const SIRET_DIGITS = /^\d{14}$/;
const SIREN_DIGITS = /^\d{9}$/;

/** A DNS label, as a URL parser hands it back: lowercase, punycode. */
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** Letters only, which is also what rules an IP address out. */
const TOP_LEVEL_DOMAIN = /^[a-z]{2,}$/;

/**
 * The characters a stored LinkedIn handle is made of — the set the API
 * extracts handles with. A handle outside it could never be matched.
 */
const LINKEDIN_HANDLE = /^[a-zA-Z0-9_%.-]+$/;

/**
 * Reads the message as a web address, with or without its scheme. Refuses
 * what a URL parser would accept but nobody types as a site: whitespace, an
 * `@` (an e-mail, or credentials in front of a host), a scheme other than
 * http(s).
 */
const parseWebAddress = (text: string): URL | null => {
  if (/\s/.test(text) || text.includes("@")) {
    return null;
  }

  const hasHttpScheme = /^https?:\/\//i.test(text);
  if (!hasHttpScheme && text.includes("://")) {
    return null;
  }

  try {
    return new URL(hasHttpScheme ? text : `https://${text}`);
  } catch {
    return null;
  }
};

const isLinkedInHost = (host: string): boolean =>
  host === "linkedin.com" || host.endsWith(".linkedin.com");

/**
 * A company or school page. Its sub-pages (`/about/`, `/jobs/`), query and
 * fragment are dropped; a member profile, a showcase page or any other path is
 * not a company identifier.
 */
const parseLinkedInPage = (url: URL): CompanyIdentifier | null => {
  const [kind, handle] = url.pathname.split("/").filter(Boolean);
  const linkedinType = kind?.toLowerCase();

  if (linkedinType !== "company" && linkedinType !== "school") {
    return null;
  }
  if (!handle || !LINKEDIN_HANDLE.test(handle)) {
    return null;
  }

  return { type: "linkedin", value: handle, linkedinType };
};

const isExcludedDomain = (domain: string): boolean =>
  COMPANY_IDENTIFIER_EXCLUDED_DOMAINS.some(
    (excluded) => domain === excluded || domain.endsWith(`.${excluded}`),
  );

/** A company's own website: a real domain name, not a platform's. */
const parseWebsite = (host: string): CompanyIdentifier | null => {
  const domain = host.startsWith("www.") ? host.slice("www.".length) : host;
  const labels = domain.split(".");

  if (labels.length < 2 || !labels.every((label) => HOST_LABEL.test(label))) {
    return null;
  }
  if (!TOP_LEVEL_DOMAIN.test(labels[labels.length - 1])) {
    return null;
  }
  if (isExcludedDomain(domain)) {
    return null;
  }

  return { type: "domain", value: domain };
};

/**
 * Recognises a message that is nothing but a company identifier: a SIRET, a
 * SIREN, a website (a domain, or the address of any of its pages) or the
 * address of a LinkedIn company or school page.
 *
 * The whole trimmed message must be the identifier. A sentence that merely
 * contains one — "trouve-moi l'entreprise 123 456 789" — is a question for the
 * chat and returns `null`, as does anything that is not an identifier at all.
 */
export const parseCompanyIdentifier = (
  text: string,
): CompanyIdentifier | null => {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > COMPANY_IDENTIFIER_MAX_LENGTH) {
    return null;
  }

  const digits = trimmed.replace(REGISTRY_NUMBER_SEPARATORS, "");
  if (SIRET_DIGITS.test(digits)) {
    return { type: "siret", value: digits };
  }
  if (SIREN_DIGITS.test(digits)) {
    return { type: "siren", value: digits };
  }

  const url = parseWebAddress(trimmed);
  if (!url) {
    return null;
  }

  return isLinkedInHost(url.hostname)
    ? parseLinkedInPage(url)
    : parseWebsite(url.hostname);
};

/**
 * Shortest and longest text still read as a company name typed alone. The cap
 * is the one `POST /companies/identify-by-name` puts on its body: past it a
 * message is prose, and no company is named that.
 */
export const COMPANY_NAME_MIN_LENGTH = 2;
export const COMPANY_NAME_MAX_LENGTH = 80;

/**
 * Most words a company name may hold. "Compagnie Générale des Établissements
 * Michelin" is five; a seventh word is a sentence someone is writing to the
 * chat, and looking it up would cost a query per message.
 */
export const COMPANY_NAME_MAX_WORDS = 6;

/**
 * What a company name is written with: letters of any script, digits, and the
 * handful of signs brands carry — `&`, an apostrophe, a dot, a dash, a plus.
 * Everything else — a question mark, a comma, a slash, a colon — is
 * punctuation nobody puts in a name and the mark of a message meant for the
 * chat.
 */
const COMPANY_NAME_CHARACTERS = /^[\p{L}\p{M}\p{N} '’&.+-]+$/u;

/** A name has to name something: digits alone are a number, not a company. */
const HAS_A_LETTER = /\p{L}/u;

/**
 * Reads a message as the name of a company, typed alone — the last resort of
 * the composer's rule, tried only once `parseCompanyIdentifier` has refused
 * the message.
 *
 * A name designates nothing on its own: unlike a SIRET, it is only ever
 * resolved against the companies Shaarp already holds, and a message no
 * company is named goes to the chat untouched. What this decides is therefore
 * narrow — whether the text is worth one indexed lookup — and it is kept
 * deliberately tight so that writing to the chat costs nothing.
 *
 * Returns the name with its spacing normalised, the form the lookup matches
 * on, or `null`.
 */
export const parseCompanyName = (text: string): string | null => {
  const name = text.trim().replace(/\s+/g, " ");

  if (
    name.length < COMPANY_NAME_MIN_LENGTH ||
    name.length > COMPANY_NAME_MAX_LENGTH
  ) {
    return null;
  }
  if (!COMPANY_NAME_CHARACTERS.test(name) || !HAS_A_LETTER.test(name)) {
    return null;
  }
  if (name.split(" ").length > COMPANY_NAME_MAX_WORDS) {
    return null;
  }

  return name;
};
